import React from 'react';
import { AlertTriangle, Activity, ShieldCheck, Users } from 'lucide-react';
import {
  ViewsGallery,
  useRecipeUrlState,
  applyRecipeLenses,
  clearRecipeLenses,
  type IView,
  type IViewGroup,
  type RecipeLensSpec,
} from '../../../core/assistant';

/**
 * ProcessViews — named, one-click composite "views" for process maps, built
 * on the shared View primitive (the Blueprint "View Box" / "Views gallery"
 * model). Each view composes several process view-rule lenses at once to
 * answer a concern. Deep-linkable via ?review=<id>.
 */

const view = (
  id: string,
  title: string,
  description: string,
  icon: IView['icon'],
  tone: IView['tone'],
  specs: RecipeLensSpec[],
): IView => ({
  id,
  title,
  description,
  icon,
  tone,
  group: 'Reviews',
  apply: () => applyRecipeLenses(specs),
  clear: clearRecipeLenses,
});

export const PROCESS_VIEWS: readonly IView[] = [
  view(
    'bottleneck-review',
    'Bottleneck Review',
    'Heatmap by cycle time with SLA labelled — the slowest steps render red.',
    AlertTriangle,
    'red',
    [
      { kind: 'heatmap', field: 'cycleTimeMs', minColor: '#dbeafe', maxColor: '#1e40af' },
      { kind: 'label', field: 'slaMs' },
    ],
  ),
  view(
    'sla-risk',
    'SLA Risk',
    'Heatmap by step SLA with cycle time labelled — surface steps breaching their target.',
    Activity,
    'amber',
    [
      { kind: 'heatmap', field: 'slaMs', minColor: '#dcfce7', maxColor: '#dc2626' },
      { kind: 'label', field: 'cycleTimeMs' },
    ],
  ),
  view(
    'compliance-audit',
    'Compliance Audit',
    'Colour by control status with framework labelled — find failing or untested controls.',
    ShieldCheck,
    'blue',
    [
      { kind: 'colorBy', field: 'controlStatus' },
      { kind: 'label', field: 'framework' },
    ],
  ),
  view(
    'ownership-gaps',
    'Ownership Gaps',
    'Colour by RACI status with owner labelled — spot unassigned or ambiguous steps.',
    Users,
    'slate',
    [
      { kind: 'colorBy', field: 'raciStatus' },
      { kind: 'label', field: 'owner' },
    ],
  ),
];

export const PROCESS_VIEW_GROUPS: readonly IViewGroup[] = [{ views: PROCESS_VIEWS }];

/** Active process view, deep-linked via ?review=. Shared by the View Box
 *  hero and the Views gallery so they always agree. */
export const useProcessViewState = () =>
  useRecipeUrlState(PROCESS_VIEWS, { param: 'review' });

export const ProcessRecipes: React.FC = () => {
  const { activeRecipeId, selectRecipe } = useProcessViewState();
  return (
    <ViewsGallery
      groups={PROCESS_VIEW_GROUPS}
      activeViewId={activeRecipeId}
      onSelect={(v) => selectRecipe(v.id)}
      allowDeselect
      onClear={() => selectRecipe(null)}
    />
  );
};
